import { UserManager, UserManagerSettings, WebStorageStateStore } from "oidc-client-ts";

const appServicesAuthLogoutUrl = ".auth/logout?post_logout_redirect_uri=/";

/**
 * Detects whether the app is running inside an iframe (e.g. embedded in MkDocs).
 * When true, popup-based OIDC flows should be used instead of redirects,
 * because the Keycloak IDP blocks redirect flows in iframes via X-Frame-Options.
 */
export const isInIframe = (): boolean => {
    try {
        return window.self !== window.top;
    } catch {
        // SecurityError thrown when cross-origin — means we ARE in an iframe
        return true;
    }
};

/**
 * Detects Firefox. Firefox blocks localStorage/sessionStorage access for cross-origin
 * iframes when tracking protection / cookie blocking is active, which makes the OIDC
 * init throw "The operation is insecure". We surface a targeted hint on the login screen
 * only for Firefox-in-iframe so users know how to unblock site data.
 */
export const isFirefox = (): boolean => {
    return typeof navigator !== "undefined" && navigator.userAgent.toLowerCase().includes("firefox");
};

interface AuthSetup {
    // Set to true if login elements should be shown in the UI
    useLogin: boolean;
    // Set to true if access control is enforced by the application
    requireAccessControl: boolean;
    // Set to true if the application allows unauthenticated access (only applies for documents without access control)
    enableUnauthenticatedAccess: boolean;
    // OIDC provider configuration returned by /auth_setup
    oidcConfig: {
        authority: string; // OIDC issuer URL (e.g. Keycloak realm URL)
        clientId: string; // OIDC client ID registered with the provider
        redirectUri: string; // Redirect URI after login
        postLogoutRedirectUri: string; // Redirect URI after logout
        scope: string; // Space-separated OIDC scopes
        useTokenProxy?: boolean; // If true, route token requests through backend proxy
    };
}

// Fetch the auth setup JSON data from the API if not already cached
async function fetchAuthSetup(): Promise<AuthSetup> {
    const response = await fetch("/auth_setup");
    if (!response.ok) {
        throw new Error(`auth setup response was not ok: ${response.status}`);
    }
    return await response.json();
}

const authSetup = await fetchAuthSetup();

export const useLogin = authSetup.useLogin;

export const requireAccessControl = authSetup.requireAccessControl;

export const enableUnauthenticatedAccess = authSetup.enableUnauthenticatedAccess;

export const requireLogin = requireAccessControl && !enableUnauthenticatedAccess;

// Build the OIDC UserManager config from /auth_setup response
export const oidcConfig = authSetup.oidcConfig;

// Build an absolute redirect URI using the current window's location and the relative redirect URI from auth setup
export const getRedirectUri = () => {
    return window.location.origin + authSetup.oidcConfig.redirectUri;
};

// Sign out of app services easy-auth (fallback when not using OIDC popup)
export const appServicesLogout = () => {
    window.location.href = appServicesAuthLogoutUrl;
};

/**
 * Creates an oidc-client-ts UserManager configured for the KUMS-MFA / Keycloak provider.
 * Call this once at app startup (see index.tsx).
 */
export const createUserManager = (): UserManager => {
    const settings: UserManagerSettings = {
        authority: oidcConfig.authority,
        client_id: oidcConfig.clientId,
        redirect_uri: window.location.origin + oidcConfig.redirectUri,
        post_logout_redirect_uri: window.location.origin + oidcConfig.postLogoutRedirectUri,
        popup_redirect_uri: window.location.origin + "/popup-callback.html",
        popup_post_logout_redirect_uri: window.location.origin + "/popup-callback.html",
        response_type: "code",
        scope: oidcConfig.scope || "openid profile email client-roles",
        userStore: new WebStorageStateStore({ store: window.localStorage }),
        // Use localStorage for state so BroadcastChannel fallback works across tabs
        stateStore: new WebStorageStateStore({ store: window.localStorage }),
        // Renew the access token in the background before it expires so long
        // chat sessions don't hit a 403 on a follow-up request when the token
        // lapses. Start renewal 60s before expiry (also covers clock skew and
        // request latency through the Zscaler proxy).
        automaticSilentRenew: true,
        accessTokenExpiringNotificationTimeInSeconds: 60
    };

    // Route token requests through backend proxy for confidential OIDC clients
    if (oidcConfig.useTokenProxy) {
        settings.metadata = {
            issuer: oidcConfig.authority,
            authorization_endpoint: oidcConfig.authority + "/protocol/openid-connect/auth",
            token_endpoint: window.location.origin + "/auth/token",
            end_session_endpoint: oidcConfig.authority + "/protocol/openid-connect/logout",
            userinfo_endpoint: oidcConfig.authority + "/protocol/openid-connect/userinfo",
            jwks_uri: oidcConfig.authority + "/protocol/openid-connect/certs"
        };
    }

    return new UserManager(settings);
};

/**
 * Checks if the user is currently logged in via the OIDC UserManager.
 * @param {UserManager | undefined} userManager - The oidc-client-ts UserManager instance.
 * @returns {Promise<boolean>} Resolves to true if the user has a valid session.
 */
export const checkLoggedIn = async (userManager: UserManager | undefined): Promise<boolean> => {
    if (!userManager) return false;
    const user = await userManager.getUser();
    return user !== null && !user.expired;
};

/**
 * Gets the OIDC access token for use with backend API calls.
 * Attempts silent renewal if the current token is expired.
 * @param {UserManager | undefined} userManager - The oidc-client-ts UserManager instance.
 * @returns {Promise<string | undefined>} The access token string, or undefined if not authenticated.
 */
// Renew the token if it is already expired OR will expire within this many
// seconds. Renewing slightly early avoids the race where a token that is valid
// at request-build time has expired by the time it reaches the backend (network
// latency, proxy, clock skew) -> backend rejects it with 403.
const TOKEN_EXPIRY_SKEW_SECONDS = 60;

export const getToken = async (userManager: UserManager | undefined): Promise<string | undefined> => {
    if (!userManager) return undefined;
    let user = await userManager.getUser();
    if (!user) return undefined;
    const expiresInSeconds = user.expires_in;
    const expiringSoon = expiresInSeconds !== undefined && expiresInSeconds <= TOKEN_EXPIRY_SKEW_SECONDS;
    if (user.expired || expiringSoon) {
        try {
            user = await userManager.signinSilent();
        } catch {
            return undefined;
        }
    }
    return user?.access_token;
};

/**
 * Gets the preferred username from the OIDC user profile.
 * @param {UserManager | undefined} userManager - The oidc-client-ts UserManager instance.
 * @returns {Promise<string | null>} The username, or null if unavailable.
 */
export const getUsername = async (userManager: UserManager | undefined): Promise<string | null> => {
    if (!userManager) return null;
    const user = await userManager.getUser();
    return user?.profile?.preferred_username ?? user?.profile?.email ?? null;
};

export const getFirstName = async (userManager: UserManager | undefined): Promise<string | null> => {
    if (!userManager) return null;
    const user = await userManager.getUser();
    return (user?.profile?.given_name as string) ?? null;
};

/**
 * Gets the OIDC ID token claims from the current user's profile.
 * @param {UserManager | undefined} userManager - The oidc-client-ts UserManager instance.
 * @returns {Promise<Record<string, unknown> | undefined>} The token claims, or undefined if not authenticated.
 */
export const getTokenClaims = async (userManager: UserManager | undefined): Promise<Record<string, unknown> | undefined> => {
    if (!userManager) return undefined;
    const user = await userManager.getUser();
    const profile = (user?.profile as Record<string, unknown>) ?? {};

    // Decode access token and always show all AT claims with [at] prefix for full visibility
    if (user?.access_token) {
        try {
            const parts = user.access_token.split(".");
            const payload = JSON.parse(atob(parts[1].replace(/-/g, "+").replace(/_/g, "/")));
            for (const [key, value] of Object.entries(payload)) {
                profile[`[at] ${key}`] = value;
            }
        } catch {
            profile["[at] decode_error"] = "Could not decode access token";
        }
    }

    return Object.keys(profile).length > 0 ? profile : undefined;
};
